import React, { useState } from 'react';
import { entriesApi, templateApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Download } from 'lucide-react';
import Papa from 'papaparse';

export function BulkUploadDialog({ open, onOpenChange, onSuccess }) {
  const [bulkData, setBulkData] = useState([]);
  const [bulkValidation, setBulkValidation] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, { header: true, skipEmptyLines: true, complete: (results) => { setBulkData(results.data); setBulkValidation(null); } });
  };

  const handleBulkValidate = async () => {
    const rows = bulkData.map(r => ({
      date: r.date || '', site_name: r.site_name || '', site_id: r.site_id || '',
      entry_type: r.entry_type || '', category: r.category || '', sub_category: r.sub_category || '',
      entity_name: r.entity_name || '', payment_mode: r.payment_mode || 'cash',
      amount: r.amount || '0', notes: r.notes || ''
    }));
    try { const { data } = await entriesApi.bulkValidate(rows); setBulkValidation(data); } catch {}
  };

  const handleBulkUpload = async () => {
    if (!bulkValidation) return;
    setBulkUploading(true);
    const validRows = bulkValidation.results.filter(r => r.valid).map(r => r.data);
    try {
      await entriesApi.bulkUpload(validRows);
      onOpenChange(false);
      setBulkData([]);
      setBulkValidation(null);
      onSuccess?.();
    } catch {}
    setBulkUploading(false);
  };

  const handleClose = (v) => {
    if (!v) { setBulkData([]); setBulkValidation(null); }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Bulk Upload Entries</DialogTitle><DialogDescription className="sr-only">Upload entries via CSV file</DialogDescription></DialogHeader>
        <div className="space-y-4" data-testid="bulk-upload-form">
          <div className="flex items-center gap-4">
            <a href={templateApi.csv()} className="text-sm text-foreground underline flex items-center gap-1" data-testid="download-template">
              <Download className="w-3.5 h-3.5" /> Download Template
            </a>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Upload CSV File</Label>
            <Input type="file" accept=".csv" onChange={handleFileUpload} className="mt-1.5" data-testid="bulk-file-input" />
          </div>
          {bulkData.length > 0 && (
            <>
              <div className="text-sm text-muted-foreground">{bulkData.length} rows found</div>
              <div className="overflow-x-auto max-h-64 border border-border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted">
                    <tr>
                      <th className="py-2 px-2 text-left">#</th>
                      <th className="py-2 px-2 text-left">Date</th>
                      <th className="py-2 px-2 text-left">Site</th>
                      <th className="py-2 px-2 text-left">Type</th>
                      <th className="py-2 px-2 text-left">Category</th>
                      <th className="py-2 px-2 text-left">Item</th>
                      <th className="py-2 px-2 text-left">Amount</th>
                      {bulkValidation && <th className="py-2 px-2 text-left">Status</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {bulkData.map((row, i) => {
                      const vr = bulkValidation?.results?.[i];
                      return (
                        <tr key={i} className={`border-b border-border ${vr && !vr.valid ? 'bg-red-50 dark:bg-red-950/20' : ''}`}>
                          <td className="py-1.5 px-2">{i + 1}</td>
                          <td className="py-1.5 px-2">{row.date}</td>
                          <td className="py-1.5 px-2">{row.site_name || row.site_id}</td>
                          <td className="py-1.5 px-2">{row.entry_type}</td>
                          <td className="py-1.5 px-2">{row.category}</td>
                          <td className="py-1.5 px-2">{row.sub_category || '-'}</td>
                          <td className="py-1.5 px-2 font-mono">{row.amount}</td>
                          {vr && (
                            <td className="py-1.5 px-2">
                              {vr.valid ? <span className="text-emerald-600">Valid</span> : <span className="text-red-600">{vr.errors.join(', ')}</span>}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2">
                {!bulkValidation && <Button onClick={handleBulkValidate} data-testid="bulk-validate-btn">Validate Data</Button>}
                {bulkValidation && bulkValidation.valid_count > 0 && (
                  <Button onClick={handleBulkUpload} disabled={bulkUploading} data-testid="bulk-confirm-btn">
                    {bulkUploading ? 'Uploading...' : `Upload ${bulkValidation.valid_count} Valid Entries`}
                  </Button>
                )}
                {bulkValidation && (
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-emerald-600">{bulkValidation.valid_count} valid</span>
                    <span className="text-red-600">{bulkValidation.error_count} errors</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
